import { SalaryComponent } from 'components/SalaryComponents/types';
import { SalaryStructureComponent } from 'components/SalaryStructure/types';
import { useFormWithProvider } from 'forms';
import { INITIAL_FORM_DATA } from '../constants';
import { SalaryStructureFormData, StructureContribution } from '../types';

export const useSalaryStructure = () => {
  const { formData, setFormData, setFieldValue, register } =
    useFormWithProvider<SalaryStructureFormData>();

  const setName = (name: string) => {
    setFieldValue('name', name);
  };

  const setDescription = (description: string) => {
    setFieldValue('description', description);
  };

  const addComponentToStructure = (component: SalaryStructureComponent) => {
    const newComponents = { ...(formData?.components || {}), [component.id]: component };
    setFormData({
      ...formData,
      components: newComponents,
    });
  };

  const addComponentsByIds = (ids: string[], availableComponents: SalaryComponent[]) => {
    let structureComponents = formData?.components || {};
    const componentIds = formData?.componentIds || [];
    ids.forEach((id) => {
      const exists = id in structureComponents;
      if (!exists) {
        const matchingComponent = availableComponents.find((comp) => comp.id === id);
        if (matchingComponent) {
          structureComponents = {
            ...structureComponents,
            [matchingComponent.id]: {
              id: matchingComponent.id,
              salaryComponent: matchingComponent,
              valueType: 'SALARY_STRUCTURE_COMPONENT_VALUE_TYPE_CTC',
              salaryComponentId: matchingComponent.id,
            },
          };
          componentIds.push(matchingComponent.id);
        }
      }
    });

    setFormData({
      ...formData,
      components: structureComponents,
      componentIds: componentIds,
    });
  };

  const updateComponentById = (id: string, updatedProps: Partial<SalaryStructureComponent>) => {
    const structureComponents = formData?.components || {};
    const exists = id in structureComponents;
    if (!exists) return;
    structureComponents[id] = { ...structureComponents[id], ...updatedProps };

    setFormData({
      ...formData,
      components: structureComponents,
    });
  };

  const updateFullComponent = (id: string, updatedProps: Partial<SalaryStructureComponent>) => {
    const structureComponents = formData?.components || {};
    const exists = id in structureComponents;
    if (!exists) return;
    structureComponents[id] = { ...structureComponents[id], ...updatedProps };

    setFormData({
      ...formData,
      components: structureComponents,
    });
  };

  const removeComponentById = (id: string) => {
    const structureComponents = formData?.components || {};
    let componentIds = formData?.componentIds || [];
    const exists = id in structureComponents;
    if (!exists) return;
    delete structureComponents[id];
    componentIds = componentIds.filter((compId) => compId !== id);

    setFormData({
      ...formData,
      components: structureComponents,
      componentIds: componentIds,
    });
  };

  const setEmployerContributions = (contributions: StructureContribution[]) => {
    const structureContributions = formData?.contributions || {};
    const contributionIds = formData?.contributionIds || [];
    contributions.forEach((contribution) => {
      const exists = contribution.componentId in structureContributions;
      if (!exists) {
        structureContributions[contribution.componentId] = contribution;
        contributionIds.push(contribution.componentId);
      }
    });

    setFormData({
      ...formData,
      contributions: structureContributions,
      contributionIds: contributionIds,
    });
  };

  const removeContributionById = (id: string) => {
    let contributionIds = formData?.contributionIds || [];
    const structureContributions = formData?.contributions || {};
    const exists = id in structureContributions;
    if (!exists) return;
    delete structureContributions[id];
    contributionIds = contributionIds.filter((compId) => compId !== id);

    setFormData({
      ...formData,
      contributions: structureContributions,
      contributionIds: contributionIds,
    });
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM_DATA);
  };

  const salaryStructureComponents = Object.values(formData.components || {});

  const earningComponents = salaryStructureComponents.filter(
    (comp) =>
      comp.salaryComponent.category === 'EARNINGS_CATEGORY' &&
      comp.salaryComponent.settings.earningDetails.payType === 'fixed',
  );

  const variableComponents = salaryStructureComponents.filter((comp) => {
    return (
      comp.salaryComponent.category === 'EARNINGS_CATEGORY' &&
      comp.salaryComponent.settings.earningDetails.payType === 'variable'
    );
  });

  const nonPayableBenefitsComponents = salaryStructureComponents.filter(
    (comp) => 
      comp.salaryComponent.category === 'EARNINGS_CATEGORY' &&
      'earningDetails' in comp.salaryComponent.settings &&
      comp.salaryComponent.settings.earningDetails?.payType === 'non_payable_benefit',
  );

  const employerContributions = Object.values(
    formData.contributions || {},
  ) as StructureContribution[];

  return {
    formData,
    salaryStructureComponents,
    setName,
    setDescription,
    addComponentToStructure,
    addComponentsByIds,
    updateComponentById,
    updateFullComponent,
    removeComponentById,
    setEmployerContributions,
    resetForm,
    earningComponents,
    variableComponents,
    nonPayableBenefitsComponents,
    employerContributions,
    removeContributionById,
    setFormData,
    setFieldValue,
    register,
  };
};
